import React, { useEffect, useMemo, useState } from 'react'
import { Button, notification } from 'antd'
import idx from 'idx'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Route } from 'react-router-dom'

import { fetchFooterList, resetFooterStatus, updateFooter } from '~actions'
import { ResultModal } from '~components'
import { getCurrentUser } from '~context'
import { PageLayout } from '~stories'

import FooterForm, { EMPTY_FOOTER_PAYLOAD, getFooterValidationErrors } from '../FooterForm/FooterForm'

const NOTIFICATION_MESSAGES = {
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'Footer record not found.',
  REQUIRED_FIELDS: 'Please complete all required footer fields before saving.'
}

const EditFooter = ({
  fetchFooterList,
  footers,
  isSaving,
  resetFooterStatus,
  route,
  updateFooter,
  updateMessage,
  updateStatus
}) => {
  const [footerToEdit, setFooterToEdit] = useState(EMPTY_FOOTER_PAYLOAD)
  const [footerId, setFooterId] = useState('')
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()

  const selectedFooter = useMemo(() => {
    if (!footerId || !Array.isArray(footers)) return null
    return footers.find(footer => String(footer._id) === String(footerId))
  }, [footers, footerId])

  useEffect(() => {
    const currentFooterId = idx(route, _ => _.match.params.id)
    if (currentFooterId) {
      setFooterId(currentFooterId)
      fetchFooterList()
    }
  }, [fetchFooterList, route])

  useEffect(() => {
    if (selectedFooter) {
      setFooterToEdit(selectedFooter)
    }
  }, [selectedFooter])

  useEffect(() => {
    if (!updateStatus && updateMessage) {
      notification.error({ message: updateMessage })
      resetFooterStatus()
    }
  }, [resetFooterStatus, updateMessage, updateStatus])

  const onSubmit = () => {
    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }

    if (!selectedFooter) {
      notification.error({ message: NOTIFICATION_MESSAGES.NOT_FOUND })
      return
    }

    const validationErrors = getFooterValidationErrors(footerToEdit)
    if (validationErrors.length > 0) {
      notification.error({
        description: validationErrors.slice(0, 3).join(' '),
        message: NOTIFICATION_MESSAGES.REQUIRED_FIELDS
      })
      return
    }

    updateFooter(footerId, footerToEdit, accessToken)
  }

  const keepEditing = (
    <Route key='editFooter' render={({ history }) => (
      <Button
        onClick={() => {
          resetFooterStatus()
          history.push(`/footer/${footerId}/edit`)
        }}
        type='primary'
      >
        Keep Editing
      </Button>
    )} />
  )

  const goToFooterListButton = (
    <Route key='listFooter' render={({ history }) => (
      <Button
        onClick={() => {
          resetFooterStatus()
          history.push('/footer')
        }}
        type='primary'
      >
        Go to Footer List
      </Button>
    )} />
  )

  return (
    <PageLayout extra={[<Button key='update_footer' loading={isSaving} onClick={onSubmit} type='primary'>Update</Button>]} title='Footer (MSG)'>
      <FooterForm footer={footerToEdit} setFooter={setFooterToEdit} />
      {updateStatus && (
        <ResultModal
          actions={[goToFooterListButton, keepEditing]}
          handleCancel={resetFooterStatus}
          status='success'
          title={updateMessage}
        />
      )}
    </PageLayout>
  )
}

EditFooter.propTypes = {
  fetchFooterList: PropTypes.func,
  footers: PropTypes.array,
  isSaving: PropTypes.bool,
  resetFooterStatus: PropTypes.func,
  route: PropTypes.object,
  updateFooter: PropTypes.func,
  updateMessage: PropTypes.string,
  updateStatus: PropTypes.bool
}

EditFooter.defaultProps = {
  footers: []
}

const mapStateToProps = ({ footer }) => ({
  footers: footer.footers,
  isSaving: footer.isSaving,
  updateMessage: footer.message,
  updateStatus: footer.success
})

const mapDispatchToProps = {
  fetchFooterList,
  resetFooterStatus,
  updateFooter
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditFooter)
